import * as React from "react";
import { LogOut, ChevronRight, Settings, ChevronDown, HelpCircle, Wand2, Search, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/primitives/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@repo/ui/primitives/dialog";
import { Button } from "@repo/ui/primitives/button";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import { languageMeta } from "@/lib/i18n.config";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { NotificationBell } from "@/components/common/NotificationBell";
import { SidebarTrigger } from "@repo/ui/primitives/sidebar";
import { useHelperTourStore } from "@/store/helperTour.store";
import { useTenantStore } from "@/store/tenant.store";
import SettingsModal from "@/components/common/SettingsModal";
import { SetupGuideModal } from "@/components/common/SetupGuideModal";
import { authIntegration } from "@/lib/api/client";
import { cn } from "@/lib/utils";
import { OtpCountdownHint } from "@/components/auth/OtpCountdownHint";
import { useOtpCountdown } from "@/hooks/useOtpCountdown.js";

export function Header({
  user,
  onLogout,
  breadcrumb,
  moduleIcon: ModuleIcon,
  dockPages = [],
  dockPreferenceKey = null,
  onOpenCommandCenter,
  breadcrumbInfoRef = null,
}) {
  const { t } = useTranslation("header");
  const { startTour } = useHelperTourStore();
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const setSelectedTenantId = useTenantStore((state) => state.setSelectedTenantId);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [isSetupModalOpen, setIsSetupModalOpen] = React.useState(false);
  const [switchChallenge, setSwitchChallenge] = React.useState(null);
  const [switchCode, setSwitchCode] = React.useState("");
  const [switchBusy, setSwitchBusy] = React.useState(false);
  const [switchIssuedAt, setSwitchIssuedAt] = React.useState(null);
  const [switchAttemptsRemaining, setSwitchAttemptsRemaining] = React.useState(null);
  const memberships = user?.memberships;
  const currentLanguageCode = String(i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const nextLanguage = languageMeta.find((language) => language.code !== currentLanguageCode) || languageMeta[0];
  const breadcrumbDestinations = React.useMemo(
    () => new Set(dockPages.map((page) => page.url)),
    [dockPages],
  );

  const switchCountdown = useOtpCountdown({
    issuedAt: switchChallenge ? switchIssuedAt : null,
    expiresInSec: switchChallenge?.expiresInSec || 300,
    resendCooldownSec: switchChallenge?.resendCooldownSec || 60,
  });

  const handleStartTour = () => {
    void i18n.loadNamespaces("helperTour").finally(() => {
      startTour();
    });
  };

  const applyTenantSwitch = (tenantId) => {
    setSelectedTenantId(tenantId);
    setSwitchChallenge(null);
    setSwitchCode("");
    setSwitchIssuedAt(null);
    setSwitchAttemptsRemaining(null);
  };

  const handleTenantSwitch = async (tenantId) => {
    if (!tenantId || tenantId === selectedTenantId || switchBusy) return;
    setSwitchBusy(true);
    try {
      const result = await authIntegration.evaluateTenantSwitch(tenantId);
      if (!result.success) return;
      if (result.data?.mfaRequired) {
        setSwitchChallenge(result.data);
        setSwitchCode("");
        setSwitchIssuedAt(Date.now());
        setSwitchAttemptsRemaining(null);
        return;
      }
      applyTenantSwitch(result.data?.targetTenantId || tenantId);
    } finally {
      setSwitchBusy(false);
    }
  };

  const handleVerifyTenantSwitch = async () => {
    if (!switchChallenge?.challengeId || !switchCode.trim()) return;
    setSwitchBusy(true);
    try {
      const result = await authIntegration.verifyTenantSwitch({
        challengeId: switchChallenge.challengeId,
        code: switchCode.trim(),
        channel: switchChallenge.channel,
      });
      if (result.success) {
        applyTenantSwitch(result.data?.targetTenantId || switchChallenge.targetTenantId);
      } else if (typeof result.attemptsRemaining === "number") {
        setSwitchAttemptsRemaining(result.attemptsRemaining);
      }
    } finally {
      setSwitchBusy(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-[var(--ds-layer-shell)] flex h-[var(--ds-header-height)] min-h-[var(--ds-header-height)] shrink-0 items-center border-b border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] ease-linear motion-reduce:transition-none"
      data-helper="header"
    >
      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-3 lg:px-6">
        <div className="flex min-w-0 items-center gap-4">
          {user && <SidebarTrigger className="-ml-1.5 lg:hidden" />}

          <div className="flex min-w-0 items-center gap-2">
            {breadcrumb && breadcrumb.length > 0 && (
              <nav aria-label={t("breadcrumbs.label")} className="flex min-w-0 items-center gap-1.5 overflow-hidden pt-0.5 text-xs font-medium text-sidebar-foreground/70">
                <React.Fragment>
                  <Link to="/home" aria-label={t("breadcrumbs.home")} className="flex shrink-0 items-center rounded text-sidebar-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {ModuleIcon ? (
                      <ModuleIcon className="sidebar-icon size-4 shrink-0" />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                    )}
                  </Link>
                  {breadcrumb.map((item, index) => (
                    <React.Fragment key={`${item.path}-${index}`}>
                      <ChevronRight className="h-3 w-3 shrink-0 opacity-40" aria-hidden="true" />
                      {item.current ? (
                        <span aria-current="page" className="truncate font-semibold text-sidebar-foreground">
                          {item.name}
                        </span>
                      ) : breadcrumbDestinations.has(item.path) ? (
                        <Link
                          to={item.path}
                          className={cn("truncate rounded hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", index < breadcrumb.length - 2 && "hidden sm:inline")}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <span className={cn("truncate", index < breadcrumb.length - 2 && "hidden sm:inline")}>
                          {item.name}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              </nav>
            )}
            <div ref={breadcrumbInfoRef} className="flex shrink-0 translate-y-px items-center gap-1 empty:hidden" />
          </div>
        </div>

        <div className="justify-self-center">
          {onOpenCommandCenter && (
            <button
              type="button"
              onClick={onOpenCommandCenter}
              aria-label={t("commandCenter.open", { defaultValue: "Search pages or workspaces" })}
              className="inline-flex h-[var(--ds-density-control-height)] w-10 items-center justify-center gap-2 rounded-[var(--ds-radius-control)] border border-transparent bg-muted/80 px-3 text-[13px] text-muted-foreground transition-colors hover:border-border hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none md:w-64 md:justify-start"
            >
              <Search className="size-4 shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">
                {t("commandCenter.triggerLabel", { defaultValue: "Search" })}
              </span>
              <kbd className="ml-auto hidden whitespace-nowrap font-sans text-xs/4 text-muted-foreground lg:inline">
                <span className="opacity-50">⌘</span>K
                <span className="mx-1 opacity-40">/</span>
                <span className="opacity-50">Ctrl+</span>K
              </kbd>
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-self-end gap-1 sm:gap-3">
          <NotificationBell />

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="group inline-flex min-h-[var(--ds-density-control-height)] cursor-pointer items-center rounded-[var(--ds-radius-control)] border border-sidebar-border bg-card px-2 py-1 text-left text-sidebar-foreground outline-none transition-colors hover:border-sidebar-active-border hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring motion-reduce:transition-none">
                  <div className="flex items-center gap-3 min-w-0 group">
                    <div className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground ring-offset-sidebar transition-[box-shadow] group-hover:ring-2 group-hover:ring-ring group-hover:ring-offset-2">
                      <span className="relative z-10 text-sm font-bold text-primary-foreground">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div className="hidden lg:flex lg:flex-col lg:min-w-0 text-left">
                      <p className="truncate text-sm font-semibold leading-tight text-foreground">
                        {user.name || t("userFallbackName", { defaultValue: "User" })}
                      </p>
                      <p className="truncate text-[10px] leading-tight text-muted-foreground">
                        {user.email || t("userFallbackEmail", { defaultValue: "user@example.com" })}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-1.5" sideOffset={8}>
                {user && Array.isArray(memberships) && memberships.length > 0 && (
                  <>
                    <div className="px-2 py-2 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest">
                      {t("tenant.switcherLabel", { defaultValue: "Organizations" })}
                    </div>
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                      {memberships.map((m) => {
                        const tenantId = m?.tenant?.id || m?.tenant?._id || m?.tenant;
                        const tenantName = m?.tenant?.name;
                        const isActive = tenantId === selectedTenantId;
                        if (!tenantId) return null;

                        return (
                          <DropdownMenuItem
                            key={tenantId}
                            onClick={() => handleTenantSwitch(tenantId)}
                            disabled={switchBusy}
                            className={cn(
"cursor-pointer rounded-[var(--ds-radius-control-compact)] mb-0.5",
                              isActive && "bg-primary/5 text-primary"
                            )}
                          >
                            <div className="flex items-center justify-between w-full gap-2">
                              <span className={cn(
"truncate text-sm",
                                isActive ? "font-bold" : "font-medium"
                              )}>
                                {tenantName || tenantId}
                              </span>
                              {isActive && <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                            </div>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                    <DropdownMenuSeparator className="my-1.5 opacity-50" />
                  </>
                )}

                <DropdownMenuItem
                  className="cursor-pointer rounded-[var(--ds-radius-control-compact)] mb-0.5"
                  onClick={() => setIsSettingsModalOpen(true)}
                >
                  <Settings className="h-4 w-4 mr-3 opacity-70" />
                  <span className="font-medium text-sm">{t("settingsLabel", { defaultValue: "Settings" })}</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="cursor-pointer rounded-[var(--ds-radius-control-compact)] mb-0.5"
                  onClick={() => setIsSetupModalOpen(true)}
                >
                  <Wand2 className="h-4 w-4 mr-3 opacity-70" />
                  <span className="font-medium text-sm">{t("setup.openWizard", { defaultValue: "Setup Roadmap" })}</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="cursor-pointer rounded-[var(--ds-radius-control-compact)] mb-0.5"
                  onClick={handleStartTour}
                >
                  <HelpCircle className="h-4 w-4 mr-3 opacity-70" />
                  <span className="font-medium text-sm">{t("help.startTour", { defaultValue: "Start Help Tour" })}</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="cursor-pointer rounded-[var(--ds-radius-control-compact)] mb-0.5"
                  onClick={() => void i18n.changeLanguage(nextLanguage.code)}
                >
                  <Globe className="h-4 w-4 mr-3 opacity-70" />
                  <span className="font-medium text-sm">{nextLanguage.name}</span>
                  <span className="ml-auto rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-muted-foreground">
                    {nextLanguage.code.toUpperCase()}
                  </span>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="my-1.5 opacity-50" />

                <DropdownMenuItem
                  onClick={onLogout}
                  className="cursor-pointer rounded-[var(--ds-radius-control-compact)] text-destructive focus:bg-destructive/5 focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-3 opacity-70" />
                  <span className="font-semibold text-sm">{t("logout", { defaultValue: "Logout" })}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {isSettingsModalOpen && (
        <SettingsModal
          isOpen
          onClose={() => setIsSettingsModalOpen(false)}
          dockPages={dockPages}
          dockPreferenceKey={dockPreferenceKey}
        />
      )}

      <SetupGuideModal open={isSetupModalOpen} onClose={() => setIsSetupModalOpen(false)} />

      <Dialog
        open={Boolean(switchChallenge)}
        onOpenChange={(open) => {
          if (!open) {
            setSwitchChallenge(null);
            setSwitchCode("");
            setSwitchIssuedAt(null);
            setSwitchAttemptsRemaining(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("tenant.mfaTitle", { defaultValue: "Verify organization switch" })}
            </DialogTitle>
            <DialogDescription>
              {t("tenant.mfaDescription", {
                defaultValue: "Enter the code sent to {{destination}}",
                destination: switchChallenge?.maskedDestination || "…",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="tenant-switch-code">
              {t("tenant.mfaCode", { defaultValue: "Verification code" })}
            </Label>
            <Input
              id="tenant-switch-code"
              value={switchCode}
              onChange={(e) => setSwitchCode(e.target.value)}
              maxLength={8}
              inputMode="numeric"
              autoFocus
            />
            <OtpCountdownHint
              ns="header"
              active={switchCountdown.active}
              expired={switchCountdown.expired}
              expiresLabel={switchCountdown.expiresLabel}
              canResend={switchCountdown.canResend}
              resendInSec={switchCountdown.resendInSec}
              attemptsRemaining={switchAttemptsRemaining}
              maxAttempts={switchChallenge?.maxAttempts}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSwitchChallenge(null);
                setSwitchCode("");
                setSwitchIssuedAt(null);
                setSwitchAttemptsRemaining(null);
              }}
            >
              {t("tenant.mfaCancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              disabled={switchBusy || !switchCode.trim() || switchCountdown.expired}
              onClick={handleVerifyTenantSwitch}
            >
              {t("tenant.mfaVerify", { defaultValue: "Verify" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
